import {
  ComparativeDisplayType,
  FilterConditionFormatBody,
  FilterPatchBody,
  KpiOptionObject,
  RollingType,
  UnitPrefix
} from './GlobalKpiOption'
import { TagStatusSeverity, WidgetTagObject } from './GlobalWidgetTag'
import { AdditionalSegment } from './GlobalAdditionalSegment'
import { VariantOption } from './GlobalVariantOption'
import { PeriodType } from './GlobalCustomer'
import type {
  TAttributeDataType,
  TRowWindowResult
} from '@homepal/api-contract'

import { RichText } from 'components_new/molecules/RichTextEditor/types'

export enum WidgetClass {
  DASHBOARD = 'Dashboard',
  EXPORT_TABLE = 'ExportTable',
  REPORT = 'Report'
}

export type WidgetOrder = {
  id: string | null
  index: number | null
  dir: 'asc' | 'desc'
}

export enum BreakdownByDisplayType {
  CLOSE_BY = 'CLOSE_BY',
  STACKED = 'STACKED',
  STACKED_100_PERCENT = 'STACKED_100_PERCENT'
}

export enum ComparativePeriod {
  LAST_PERIOD = 'LAST_PERIOD',
  LAST_YEAR = 'LAST_YEAR',
  AVERAGE_LAST_180_DAYS = 'AVERAGE_LAST_180_DAYS'
}

export enum DateFilterType {
  PERIOD = 'PERIOD',
  POINT = 'POINT'
}

export enum ElementType {
  KPI = 'KPI',
  TAG = 'TAG'
}

export enum PeriodFilter {
  THIS_WEEK_TO_TODAY = 'THIS_WEEK_TO_TODAY',
  THIS_MONTH_TO_TODAY = 'THIS_MONTH_TO_TODAY',
  THIS_QUARTER_TO_TODAY = 'THIS_QUARTER_TO_TODAY',
  THIS_YEAR_TO_TODAY = 'THIS_YEAR_TO_TODAY',
  TODAY = 'TODAY',
  YESTERDAY = 'YESTERDAY',
  LAST_WEEK = 'LAST_WEEK',
  LAST_MONTH = 'LAST_MONTH',
  LAST_QUARTER = 'LAST_QUARTER',
  LAST_YEAR = 'LAST_YEAR',
  LAST_7_DAYS = 'LAST_7_DAYS',
  LAST_30_DAYS = 'LAST_30_DAYS',
  LAST_90_DAYS = 'LAST_90_DAYS',
  LAST_365_DAYS = 'LAST_365_DAYS',
  LATEST_MONTH_WITH_DATA = 'LATEST_MONTH_WITH_DATA',
  LAST_3_MONTHS_TO_TODAY = 'LAST_3_MONTHS_TO_TODAY',
  LAST_6_MONTHS_TO_TODAY = 'LAST_6_MONTHS_TO_TODAY',
  LAST_12_MONTHS_TO_TODAY = 'LAST_12_MONTHS_TO_TODAY',
  LAST_24_MONTHS_TO_TODAY = 'LAST_24_MONTHS_TO_TODAY',
  LAST_36_MONTHS_TO_TODAY = 'LAST_36_MONTHS_TO_TODAY',
  LAST_2_YEARS_TO_TODAY = 'LAST_2_YEARS_TO_TODAY',
  LAST_3_YEARS_TO_TODAY = 'LAST_3_YEARS_TO_TODAY',
  LAST_4_QUARTERS_TO_TODAY = 'LAST_4_QUARTERS_TO_TODAY',
  LAST_8_QUARTERS_TO_TODAY = 'LAST_8_QUARTERS_TO_TODAY',
  LAST_12_QUARTERS_TO_TODAY = 'LAST_12_QUARTERS_TO_TODAY',

  // Custom periods
  CURRENT_FINANCIAL_YEAR = 'CURRENT_FINANCIAL_YEAR',
  PREVIOUS_FINANCIAL_YEAR = 'PREVIOUS_FINANCIAL_YEAR',
  LAST_3_FINANCIAL_YEARS_TO_TODAY = 'LAST_3_FINANCIAL_YEARS_TO_TODAY'
}

export enum ForecastPeriod {
  FILL_CURRENT_WEEK = 'FILL_CURRENT_WEEK',
  FILL_CURRENT_MONTH = 'FILL_CURRENT_MONTH',
  FILL_CURRENT_QUARTER = 'FILL_CURRENT_QUARTER',
  FILL_CURRENT_YEAR = 'FILL_CURRENT_YEAR',
  FILL_CURRENT_YEAR_AND_NEXT_YEAR = 'FILL_CURRENT_YEAR_AND_NEXT_YEAR',
  FILL_1_MONTH_AHEAD = 'FILL_1_MONTH_AHEAD',
  FILL_3_MONTH_AHEAD = 'FILL_3_MONTH_AHEAD',
  FILL_6_MONTH_AHEAD = 'FILL_6_MONTH_AHEAD',
  FILL_12_MONTH_AHEAD = 'FILL_12_MONTH_AHEAD',
  FILL_3_YEARS_AHEAD = 'FILL_3_YEARS_AHEAD',
  FILL_5_YEARS_AHEAD = 'FILL_5_YEARS_AHEAD',
  FILL_10_YEARS_AHEAD = 'FILL_10_YEARS_AHEAD',
  FILL_25_YEARS_AHEAD = 'FILL_25_YEARS_AHEAD',
  FILL_50_YEARS_AHEAD = 'FILL_50_YEARS_AHEAD'
}

export const EXTRA_LONG_FORECASTS = [
  ForecastPeriod.FILL_3_YEARS_AHEAD,
  ForecastPeriod.FILL_5_YEARS_AHEAD,
  ForecastPeriod.FILL_10_YEARS_AHEAD,
  ForecastPeriod.FILL_25_YEARS_AHEAD,
  ForecastPeriod.FILL_50_YEARS_AHEAD
]
type SupportedComparativePeriods = {
  [K in DateFilterType]: {
    [P in ComparativePeriod]: readonly PeriodFilter[]
  }
}

export const SUPPORTED_COMPARATIVE_PERIODS: SupportedComparativePeriods = {
  [DateFilterType.PERIOD]: {
    [ComparativePeriod.LAST_PERIOD]: [
      PeriodFilter.THIS_WEEK_TO_TODAY,
      PeriodFilter.THIS_MONTH_TO_TODAY,
      PeriodFilter.THIS_QUARTER_TO_TODAY,
      PeriodFilter.THIS_YEAR_TO_TODAY,
      PeriodFilter.TODAY,
      PeriodFilter.YESTERDAY,
      PeriodFilter.LAST_WEEK,
      PeriodFilter.LAST_MONTH,
      PeriodFilter.LAST_QUARTER,
      PeriodFilter.LAST_YEAR,
      PeriodFilter.LAST_7_DAYS,
      PeriodFilter.LAST_30_DAYS,
      PeriodFilter.LAST_90_DAYS,
      PeriodFilter.LAST_365_DAYS,
      PeriodFilter.LATEST_MONTH_WITH_DATA,
      PeriodFilter.LAST_3_MONTHS_TO_TODAY,
      PeriodFilter.LAST_6_MONTHS_TO_TODAY,
      PeriodFilter.LAST_12_MONTHS_TO_TODAY,
      PeriodFilter.LAST_4_QUARTERS_TO_TODAY,
      PeriodFilter.LAST_24_MONTHS_TO_TODAY,
      PeriodFilter.LAST_36_MONTHS_TO_TODAY,
      PeriodFilter.LAST_2_YEARS_TO_TODAY,
      PeriodFilter.LAST_3_YEARS_TO_TODAY,
      PeriodFilter.LAST_8_QUARTERS_TO_TODAY,
      PeriodFilter.LAST_12_QUARTERS_TO_TODAY
    ],
    [ComparativePeriod.LAST_YEAR]: [
      PeriodFilter.THIS_WEEK_TO_TODAY,
      PeriodFilter.THIS_MONTH_TO_TODAY,
      PeriodFilter.THIS_QUARTER_TO_TODAY,
      PeriodFilter.THIS_YEAR_TO_TODAY,
      PeriodFilter.TODAY,
      PeriodFilter.YESTERDAY,
      PeriodFilter.LAST_WEEK,
      PeriodFilter.LAST_MONTH,
      PeriodFilter.LAST_QUARTER,
      PeriodFilter.LAST_YEAR,
      PeriodFilter.LAST_7_DAYS,
      PeriodFilter.LAST_30_DAYS,
      PeriodFilter.LAST_90_DAYS,
      PeriodFilter.LAST_365_DAYS,
      PeriodFilter.LATEST_MONTH_WITH_DATA,
      PeriodFilter.LAST_3_MONTHS_TO_TODAY,
      PeriodFilter.LAST_6_MONTHS_TO_TODAY,
      PeriodFilter.LAST_12_MONTHS_TO_TODAY,
      PeriodFilter.LAST_4_QUARTERS_TO_TODAY,
      PeriodFilter.LAST_24_MONTHS_TO_TODAY,
      PeriodFilter.LAST_36_MONTHS_TO_TODAY,
      PeriodFilter.LAST_2_YEARS_TO_TODAY,
      PeriodFilter.LAST_3_YEARS_TO_TODAY,
      PeriodFilter.LAST_8_QUARTERS_TO_TODAY,
      PeriodFilter.LAST_12_QUARTERS_TO_TODAY,
      PeriodFilter.CURRENT_FINANCIAL_YEAR,
      PeriodFilter.PREVIOUS_FINANCIAL_YEAR
    ],
    [ComparativePeriod.AVERAGE_LAST_180_DAYS]: [
      PeriodFilter.THIS_WEEK_TO_TODAY,
      PeriodFilter.THIS_MONTH_TO_TODAY,
      PeriodFilter.TODAY,
      PeriodFilter.YESTERDAY,
      PeriodFilter.LAST_WEEK,
      PeriodFilter.LAST_MONTH,
      PeriodFilter.LAST_7_DAYS,
      PeriodFilter.LAST_30_DAYS
    ]
  },
  [DateFilterType.POINT]: {
    [ComparativePeriod.LAST_PERIOD]: [
      PeriodFilter.THIS_WEEK_TO_TODAY,
      PeriodFilter.THIS_MONTH_TO_TODAY,
      PeriodFilter.THIS_QUARTER_TO_TODAY,
      PeriodFilter.THIS_YEAR_TO_TODAY,
      PeriodFilter.TODAY,
      PeriodFilter.YESTERDAY,
      PeriodFilter.LAST_WEEK,
      PeriodFilter.LAST_MONTH,
      PeriodFilter.LAST_QUARTER,
      PeriodFilter.LAST_YEAR,
      PeriodFilter.LAST_7_DAYS,
      PeriodFilter.LAST_30_DAYS,
      PeriodFilter.LAST_90_DAYS,
      PeriodFilter.LAST_365_DAYS,
      PeriodFilter.LATEST_MONTH_WITH_DATA,
      PeriodFilter.LAST_3_MONTHS_TO_TODAY,
      PeriodFilter.LAST_6_MONTHS_TO_TODAY,
      PeriodFilter.LAST_12_MONTHS_TO_TODAY,
      PeriodFilter.LAST_4_QUARTERS_TO_TODAY,
      PeriodFilter.LAST_24_MONTHS_TO_TODAY,
      PeriodFilter.LAST_36_MONTHS_TO_TODAY,
      PeriodFilter.LAST_2_YEARS_TO_TODAY,
      PeriodFilter.LAST_3_YEARS_TO_TODAY,
      PeriodFilter.LAST_8_QUARTERS_TO_TODAY,
      PeriodFilter.LAST_12_QUARTERS_TO_TODAY,
      PeriodFilter.CURRENT_FINANCIAL_YEAR,
      PeriodFilter.PREVIOUS_FINANCIAL_YEAR
    ],
    [ComparativePeriod.LAST_YEAR]: [
      PeriodFilter.THIS_WEEK_TO_TODAY,
      PeriodFilter.THIS_MONTH_TO_TODAY,
      PeriodFilter.THIS_QUARTER_TO_TODAY,
      PeriodFilter.THIS_YEAR_TO_TODAY,
      PeriodFilter.TODAY,
      PeriodFilter.YESTERDAY,
      PeriodFilter.LAST_WEEK,
      PeriodFilter.LAST_MONTH,
      PeriodFilter.LAST_QUARTER,
      PeriodFilter.LAST_YEAR,
      PeriodFilter.LAST_7_DAYS,
      PeriodFilter.LAST_30_DAYS,
      PeriodFilter.LAST_90_DAYS,
      PeriodFilter.LAST_365_DAYS,
      PeriodFilter.LATEST_MONTH_WITH_DATA,
      PeriodFilter.LAST_3_MONTHS_TO_TODAY,
      PeriodFilter.LAST_6_MONTHS_TO_TODAY,
      PeriodFilter.LAST_12_MONTHS_TO_TODAY,
      PeriodFilter.LAST_4_QUARTERS_TO_TODAY,
      PeriodFilter.LAST_24_MONTHS_TO_TODAY,
      PeriodFilter.LAST_36_MONTHS_TO_TODAY,
      PeriodFilter.LAST_2_YEARS_TO_TODAY,
      PeriodFilter.LAST_3_YEARS_TO_TODAY,
      PeriodFilter.LAST_8_QUARTERS_TO_TODAY,
      PeriodFilter.LAST_12_QUARTERS_TO_TODAY,
      PeriodFilter.CURRENT_FINANCIAL_YEAR,
      PeriodFilter.PREVIOUS_FINANCIAL_YEAR
    ],
    [ComparativePeriod.AVERAGE_LAST_180_DAYS]: []
  }
}

export enum VisualizationConstraint {
  SMALLEST = 'SMALLEST',
  LARGEST = 'LARGEST',
  KPI_VARIABLES = 'KPI_VARIABLES'
}

export enum FunnelStatus {
  COMPLETED = 'COMPLETED',
  TERMINATED = 'TERMINATED',
  UNSET = 'UNSET'
}

export enum DatasetType {
  ENUM = 'enum',
  NUMBER = 'number',
  STRING = 'string',
  NUMBER_TAG = 'tag_number',
  DATE = 'date'
}

export enum KeyFigureVisualizationType {
  FOCUS = 'FOCUS',
  LIST = 'LIST'
}

export enum PieChartValueVisualizationType {
  VALUES = 'VALUES',
  PERCENTAGE = 'PERCENTAGE',
  VALUES_AND_PERCENTAGE = 'VALUES_AND_PERCENTAGE'
}

export enum TextAlign {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right'
}

export enum TextVerticalAlign {
  TOP = 'top',
  CENTER = 'center',
  BOTTOM = 'bottom'
}

export type SegmentPeriod = {
  from_date: string
  to_date: string
}

interface SegmentPath {
  attribute_option_id: string | null
  label: string | number | null
  filter_attribute: string
  filter_value: string | number | null
  period: SegmentPeriod | null
  attribute: string
  type: TAttributeDataType
  technical_name: string
}

export interface WidgetDataLabel extends SegmentPath {
  key: string
  path: SegmentPath[]

  // set when the row's values come from a specific metric, which is
  // what makes a row carry its own unit and number format
  kpi_option_id?: string | null

  // set when the row's values come from a specific variant option,
  // which a table with the comparison figures as rows has
  variant_option_id?: string | null
}

export interface ParsedSegmentPath extends SegmentPath {
  display_label: string
}

export interface ParsedWidgetDataLabel extends WidgetDataLabel {
  display_label: string
  path: ParsedSegmentPath[]
  index: number
}

export type StatusData = TagStatusSeverity[] | null

export type DatasetDataValue = number | number[] | string[] | null

export type Dataset = {
  breakdown_index: number
  kpi_option_id: string | null
  label: string
  data: DatasetDataValue[]
  percentages?: (number | null)[]
  statuses: StatusData[]
  tag_option_id: string | null
  type: DatasetType
  index: number
  is_comparative_data: boolean
  unit: string | null

  // holds the total of every row instead of the values of one
  // segment, which only a table with its segments as columns has
  is_total?: boolean

  // the variant option the dataset holds the values of, which only a
  // table with variant options has
  variant_option_id?: string
}

export type TKPIValue = {
  value: number
  index: number
  kpi_option_id: string
  comparative_value?: number | null
}

export type TWidgetDataTotals =
  | {
      index: number
      kpi_option_id: string
      label: string | null
      data: {
        value: number
        comparative_value: number | null
        percentage?: number | null
      }
    }[]
  | null

export type WidgetData = {
  data: Dataset[]
  labels: { data: WidgetDataLabel[]; label: string; hidden?: boolean }
  external_links: (string | null)[]
  coordinates: [number | null, number | null][]
  is_latest_exchange_rate?: boolean
  is_weighted_average?: boolean
  totals: TWidgetDataTotals
  values: TKPIValue[]

  // every metric is a row that the deeper segments nest under, so a
  // row's depth decides whether it is a metric or a segment
  expandable_metric_rows?: boolean
}

export interface IUnderlyingDataset extends Dataset {
  entity_column_id: string | null
}

export interface IUnderlyingDataResponse
  extends Omit<WidgetData, 'totals' | 'values'>,
    TRowWindowResult {
  data: IUnderlyingDataset[]
  from_date: string | null
  to_date: string | null
}

export type ListDatasetData = (number | null | string[] | number[])[]
export type DefaultDatasetData = (number | null)[]

export type TViewport = {
  latitude: number
  longitude: number
  pitch: number
  zoom: number
}

/**
 * Everything a value needs to be presented as its metric: how the
 * number is written, what unit follows it and where its thresholds
 * are. Enough on its own to format a single value.
 */
export type KpiFormat = {
  comparativeDisplayTypeSelected: ComparativeDisplayType | null
  equivalentFactor: number | null
  increaseIsPositive: boolean
  lowerBoundThreshold: number | null
  numberOfDecimals: number
  // set when the metric is formatted automatically, overrides the decimals
  significantFigures: number | null
  type: DatasetType
  unit: string | null
  unitPrefix: UnitPrefix | null
  upperBoundThreshold: number | null
}

export interface FormattedWidgetDataDataset extends KpiFormat {
  id: string
  data: DefaultDatasetData | ListDatasetData
  rawData: DatasetDataValue[]
  hidden: boolean
  index: number
  isComparativeData: boolean
  kpiOptionId?: string
  label: string
  percentages: (number | null)[] | undefined
  prettyData: (string | number | string[] | number[] | null)[]
  settingIndex: number
  showComparativeValue: boolean
  statuses: StatusData[]
  total: string | number | null
  // The total as it was measured, for comparing rather than showing
  totalRaw: number | null
  // Percentage change of the total compared to the previous period,
  // parallel to `total` (only set when comparative display includes %)
  totalPercentageDiff: number | null
  isTotal: boolean
  variantOptionId?: string
}

export type FormattedWidgetData = {
  labels: ParsedWidgetDataLabel[]
  links: (string | null)[]
  datasets: FormattedWidgetDataDataset[]
  coordinates: [number | null, number | null][]
  expandableMetricRows: boolean
}

export enum WidgetType {
  BAR_CHART = 'BAR_CHART',
  BUBBLE_CHART = 'BUBBLE_CHART',
  COMBO_CHART = 'COMBO_CHART',
  COMMENT = 'COMMENT',
  FUNNEL = 'FUNNEL',
  IMAGE = 'IMAGE',
  KEY_FIGURE = 'KEY_FIGURE',
  LINE_CHART = 'LINE_CHART',
  LIST = 'LIST',
  MAP = 'MAP',
  PIE_CHART = 'PIE_CHART',
  RICH_TEXT = 'RICH_TEXT',
  TEXT = 'TEXT'
}

type FunnelStagePut = {
  title: string
  hidden: boolean
  status: FunnelStatus
}

export type FunnelStagePutBody = FunnelStagePut[]

export type FunnelStage = {
  title: string
  hidden: boolean
  status: FunnelStatus
  index: 0
}

export type TSortByOption = {
  label: string
  value: string | boolean
  key: 'sort_by_segment' | 'sort_by_kpi_option_id' | 'sort_by_widget_tag_id'
}

export type TSortByType =
  | DatasetType.STRING
  | DatasetType.DATE
  | DatasetType.NUMBER

export interface TBaseWidget {
  id: string
  customer_id: string
  status?: TWidgetStatus
  data?: WidgetData
  dataVersion?: number // Incremented each time data is invalidated
  settings?: TWidgetSettings
  updated_at: Date
  created_at: Date
}

// These are the widgets that have access to everything in the widgets
// --> dashboards & reports
export type TCoreWidget = TBaseWidget & {
  class: WidgetClass.DASHBOARD | WidgetClass.REPORT
  image: TDashboardImage | null
  rich_text: RichText | null
  show_title: boolean
  subtitle: string
  show_subtitle: boolean
  status: TWidgetStatus
  text_align: TextAlign
  text_vertical_align: TextVerticalAlign
  title: string
  tooltip: string
  type: WidgetType
}

type TDashboardImage = {
  url: string
  name: string
}

export type TDashboardWidgetLayout = {
  width: number
  height: number
  x: number
  y: number

  // Look into when this is present.
  index?: number
}

type TDashboardWidgetCustomization = {
  background_colors: {
    default: string | null
    paper: string | null
  }
  text_colors: {
    primary: string | null
    secondary: string | null
  }
  profile_colors: {
    primary: string | null
    secondary: string | null
    tertiary: string | null
    quaternary: string | null
    quintary: string | null
    senary: string | null
    septenary: string | null
    octonary: string | null
    nonary: string | null
    denary: string | null
  }
  logotype: string | null
}

/** BASIC WIDGETS **/
export type TDashboardWidget = TCoreWidget & {
  class: WidgetClass.DASHBOARD
  dashboard_id: string
  layout: TDashboardWidgetLayout
  customization?: TDashboardWidgetCustomization
}

export const isDashboardWidget = (
  widget: TWidget
): widget is TDashboardWidget => {
  return widget.class === WidgetClass.DASHBOARD
}

export type TExportTableWidget = TBaseWidget & {
  class: WidgetClass.EXPORT_TABLE
}

export const isExportTableWidget = (
  widget: TWidget
): widget is TExportTableWidget => {
  return widget.class === WidgetClass.EXPORT_TABLE
}

export type TWidgetAuditLogBreakdownChanges = {
  original_value: number
  new_value: number
  kpi_option_index?: number
  breakdown_label: string | number | null
  column_label: {
    label: string | null
    attribute_option_id: string
  } | null
}

export type TWidgetAuditLog = {
  account_id: string
  changes: TWidgetAuditLogBreakdownChanges
}

export type TReportWidget = TCoreWidget & {
  class: WidgetClass.REPORT
  index: number
  report_block_id: string
  settings: TWidgetSettings
  audit_logs: TWidgetAuditLog[]
}

export type TWidget = TDashboardWidget | TReportWidget | TExportTableWidget

export function isCoreWidget(
  widget: TWidget
): widget is TDashboardWidget | TReportWidget {
  return (
    widget.class === WidgetClass.DASHBOARD ||
    widget.class === WidgetClass.REPORT
  )
}

/** WIDGETS WITH SETTINGS LOADED **/
export type TDashboardWidgetSettingsLoaded = TDashboardWidget & {
  settings: TWidgetSettings
}

export type TCoreWidgetSettingsLoaded =
  | TDashboardWidgetSettingsLoaded
  | TReportWidget

const isCoreWidgetSettingsLoaded = (
  widget: TCoreWidget
): widget is TCoreWidgetSettingsLoaded => {
  return widget.settings !== undefined && widget.status !== undefined
}

export const isDashboardWidgetSettingsLoaded = (
  widget: TWidget
): widget is TDashboardWidgetSettingsLoaded => {
  return (
    widget.settings !== undefined &&
    widget.status !== undefined &&
    isDashboardWidget(widget)
  )
}

export type TExportTableWidgetSettingsLoaded = TExportTableWidget & {
  settings: TWidgetSettings
  status: TWidgetStatus
}

export const isExportTableWidgetSettingsLoaded = (
  widget: TWidget
): widget is TExportTableWidgetSettingsLoaded => {
  return (
    widget.settings !== undefined &&
    widget.status !== undefined &&
    isExportTableWidget(widget)
  )
}

export type TWidgetSettingsLoaded =
  | TDashboardWidgetSettingsLoaded
  | TExportTableWidgetSettingsLoaded
  | TReportWidget

export const isWidgetSettingsLoaded = (
  widget: TWidget
): widget is TWidgetSettingsLoaded => {
  return widget.settings !== undefined && widget.status !== undefined
}

/** WIDGETS WITH DATA LOADED **/
export type TDashboardWidgetFullyLoaded = TDashboardWidgetSettingsLoaded & {
  data: WidgetData
}

export type TReportWidgetFullyLoaded = TReportWidget & { data: WidgetData }

export type TCoreWidgetFullyLoaded =
  | TDashboardWidgetFullyLoaded
  | TReportWidgetFullyLoaded

export const isCoreWidgetFullyLoaded = (
  widget: TCoreWidget
): widget is TCoreWidgetFullyLoaded => {
  return (
    widget.data !== undefined &&
    widget.data !== null &&
    isCoreWidgetSettingsLoaded(widget)
  )
}

export const isDashboardWidgetFullyLoaded = (
  widget: TWidget
): widget is TDashboardWidgetFullyLoaded => {
  return (
    widget.data !== undefined &&
    widget.data !== null &&
    isDashboardWidgetSettingsLoaded(widget)
  )
}

export const isReportWidgetFullyLoaded = (
  widget: TReportWidget
): widget is TReportWidgetFullyLoaded => {
  return widget.data !== undefined && widget.data !== null
}

export type TExportTableWidgetFullyLoaded = TExportTableWidgetSettingsLoaded & {
  data: WidgetData
}

export const isExportTableWidgetFullyLoaded = (
  widget: TWidget
): widget is TExportTableWidgetFullyLoaded => {
  return (
    widget.data !== undefined &&
    widget.data !== null &&
    isExportTableWidgetSettingsLoaded(widget)
  )
}

export type TWidgetFullyLoaded =
  | TDashboardWidgetFullyLoaded
  | TExportTableWidgetFullyLoaded

export const isWidgetFullyLoaded = (
  widget: TWidget
): widget is TWidgetFullyLoaded => {
  return (
    widget.data !== undefined &&
    widget.data !== null &&
    isWidgetSettingsLoaded(widget)
  )
}

export type TWidgetStatus = {
  broken: boolean
  inactive_metrics: string[]
  missing_data_metrics: string[]
  missing_target_metrics: string[]
  data_is_out_of_bounds: boolean
  extra_long_forecast_missing_supported_kpi: boolean
  required_slice_missing: {
    entity_id: string
    slice_id: string
  } | null
  required_definition_missing: {
    kpi_template_id: string
  } | null
  missing_period_setting: boolean
}

export type TWidgetSettings = {
  title: string
  allow_preview: boolean
  variant_options: VariantOption[]
  kpi_options: KpiOptionObject[]
  filters: FilterConditionFormatBody[]
  min_value: number | null
  max_value: number | null
  embeddable: boolean
  invert_y_axis: boolean
  is_preview: boolean
  label_chars: number | null
  show_date: boolean
  show_title: boolean
  show_subtitle: boolean
  limit_selection_to_primary_kpi: boolean
  key_figure_visualization_type: KeyFigureVisualizationType
  pie_chart_value_visualization_type: PieChartValueVisualizationType
  subtitle: string
  tooltip: string
  top_n_limit: number | null
  type: {
    selected: WidgetType
    options: { value: WidgetType; disabled: boolean }[]
  }
  tags: {
    selected: WidgetTagObject[]
    options: {
      id: string
      name: string
      ai_generated: boolean
    }[]
  }
  segment_by: {
    allow_additional_segments: boolean
    allow_click: boolean
    label: string | null
    levels: string[]
    selected: string | null
    type: TAttributeDataType
    technical_name: string | null
    additional: AdditionalSegment[]
    display_name: string | null
    stages: FunnelStage[]
  }
  sort_by: {
    available: boolean
    allow_sort_on_kpi_variable: boolean
    options: TSortByOption[]
    visualization_constraint: VisualizationConstraint | null
    selected: boolean | string | null
    type: TSortByType
  }
  date_filter: {
    type: DateFilterType
    selected: {
      is_financial_year: boolean
      from_date: string | null
      to_date: string | null
      value: PeriodFilter
    }
    options: PeriodFilter[]
    rolling_active: RollingType | null
    restrict_to_period: {
      selected: boolean
      available: boolean
      period_type: PeriodType
    }
    use_start_of_period_filter: boolean
    use_like_for_like_real_estates: boolean
    advanced_available: boolean
  }
  comparative_period: {
    available: boolean | null
    selected: {
      from_date: string
      to_date: string
      value: ComparativePeriod | null
    }
    options: ComparativePeriod[]
    increase_is_positive: boolean | null
  }
  external_link: {
    available: boolean
    show: boolean
  }
  date_filter_is_locked: boolean
  horizontal: boolean
  breakdown_by_display_type: BreakdownByDisplayType
  show_header: boolean
  show_vertical_dividers: boolean
  expanded_segment_levels: number
  show_total: boolean
  forecast_period: {
    available: boolean
    selected: ForecastPeriod | null
    options: ForecastPeriod[]
  }
  viewport: TViewport
}

export interface WidgetPatchBody {
  data: InnerWidgetPatchBody
}

export interface InnerWidgetPatchBody {
  additional_kpi_templates?: string[]
  comparative_period?: ComparativePeriod | null
  date_filter?: PeriodFilter
  element_order?: {
    id: string
    type: ElementType
  }[]
  variant_option_order?: string[]
  filters?: FilterPatchBody[] | null
  forecast_period?: ForecastPeriod | null
  image?: null
  invert_y_axis?: boolean
  is_preview?: boolean
  label_chars?: number | null
  max_value?: number | null
  min_value?: number | null
  segment_by?: string | null
  segment_group?: string
  show_date?: boolean
  embeddable?: boolean
  show_title?: boolean
  title?: string
  top_n_limit?: number | null
  type?: WidgetType
  visualization_constraint?: VisualizationConstraint | null
  widget_tags?: { id: string; index: number }[]
  date_filter_is_locked?: boolean
  horizontal?: boolean
  breakdown_by_display_type?: BreakdownByDisplayType
  restrict_to_period?: boolean
  use_start_of_period_filter?: boolean
  use_like_for_like_real_estates?: boolean
  segment_by_display_name?: string | null
  show_header?: boolean
  show_vertical_dividers?: boolean
  expanded_segment_levels?: number
  show_external_link?: boolean
  show_total?: boolean
  longitude?: number
  latitude?: number
  pitch?: number
  zoom?: number
  show_subtitle?: boolean
  tooltip?: string
  limit_selection_to_primary_kpi?: boolean
  key_figure_visualization_type?: KeyFigureVisualizationType
  pie_chart_value_visualization_type?: PieChartValueVisualizationType
  subtitle?: string
  text_align?: TextAlign
  text_vertical_align?: TextVerticalAlign
  rich_text?: RichText | null
}

export type CustomPeriodFilter = {
  from: string | null
  to: string | null
}
